import type { ReactNode } from 'react'
import { RawButton } from '@/components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { TableEmptyState } from '@/components/Table/TableEmptyState'
import { TableHeader, TableHost } from '@/components/Table'
import styles from './ClientUserManagementTable.module.css'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'

export interface ClientUserManagementTableColumn {
  id: string
  header: string
  sortable: boolean
  visible: boolean
}

export interface ClientUserManagementTableHeaderRowProps {
  columns: ClientUserManagementTableColumn[]
}

export function ClientUserManagementTableHeaderRow({
  columns,
}: ClientUserManagementTableHeaderRowProps) {
  return (
    <>
      {columns
        .filter((col) => col.visible)
        .map((col) => (
          <TableHeader key={col.id} canSort={col.sortable} align="left">
            {col.header}
          </TableHeader>
        ))}

      <TableHeader />
    </>
  )
}

interface TableUserCountProps {
  count: number
  isShowingInactiveUsers: boolean
  onInactiveChange: (value: boolean) => void
  /**
   * What the counted users are called.
   */
  noun?: string
}

const formattedCount = (count: number) => {
  return Intl.NumberFormat('en-US').format(count)
}

export function TableUserCount({
  count,
  isShowingInactiveUsers,
  onInactiveChange,
  noun = 'users',
}: TableUserCountProps) {
  return (
    <Dropdown
      autoDismiss
      position="bottom-start"
      button={
        <RawButton className={styles.userStatusDropdown}>
          {isShowingInactiveUsers ? `Inactive ${noun}` : `Active ${noun}`}
          <span data-test="users-table-count" className={styles.resultCount}>
            ({formattedCount(count ?? 0)})
          </span>
          <Icon auto name="dropdown" />
        </RawButton>
      }
      testId="user-management-active-status-dropdown"
    >
      <DropdownTitle>Users</DropdownTitle>
      <DropdownItem
        active={isShowingInactiveUsers === false}
        buttonTestId="dropdown-active-status-option"
        checkmark={isShowingInactiveUsers === false}
        onPress={() => onInactiveChange(false)}
      >
        Active
      </DropdownItem>
      <DropdownItem
        active={isShowingInactiveUsers === true}
        buttonTestId="dropdown-inactive-status-option"
        checkmark={isShowingInactiveUsers === true}
        onPress={() => onInactiveChange(true)}
      >
        Inactive
      </DropdownItem>
    </Dropdown>
  )
}

export interface ClientUserManagementTableProps {
  totalUsers: number
  /**
   * The inactive filter's selected value; `true` shows inactive users.
   */
  isShowingInactiveUsers: boolean
  onInactiveChange: (value: boolean) => void
  controls: ReactNode
  showEmptyState: boolean
  isSearchFilterApplied: boolean
  corporateMode: boolean
  onClearSearchFilter: () => void
  table: ReactNode
}

export function ClientUserManagementTable({
  totalUsers,
  isShowingInactiveUsers,
  onInactiveChange,
  controls,
  showEmptyState,
  isSearchFilterApplied,
  corporateMode,
  onClearSearchFilter,
  table,
}: ClientUserManagementTableProps) {
  return (
    <div className={styles.userTable}>
      <TableUserCount
        count={totalUsers}
        isShowingInactiveUsers={isShowingInactiveUsers}
        onInactiveChange={onInactiveChange}
      />

      <TableHost>
        {controls}

        {!showEmptyState ? (
          table
        ) : (
          <TableEmptyState bordered>
            <NoResultsEmptyState
              isSearchFilterApplied={isSearchFilterApplied}
              onClearSearchFilter={onClearSearchFilter}
              corporateMode={corporateMode}
            />
          </TableEmptyState>
        )}
      </TableHost>
    </div>
  )
}
